import type { ReactNode } from 'react';
import { type PropsWithChildren, useState } from 'react';

import { Icon } from '~/components';
import { chevron } from '~/components/styles';
import { cn } from '~/lib/cn';

export interface PlanSectionProps extends PropsWithChildren {
  title: ReactNode;
  isRequired?: boolean;
  description?: ReactNode;
  className?: string;
  isOpen?: boolean;
}

export function PlanSection({
  title,
  isRequired = true,
  description,
  children,
  className,
  isOpen: isOpenProp = false,
}: PlanSectionProps) {
  const [isOpen, setIsOpen] = useState(isOpenProp);

  return (
    <details
      className={cn(
        'w-full rounded-sm border p-xl bg-level-02 border-emphasis-subtle',
        className,
      )}
      onToggle={(e) => setIsOpen(e.currentTarget.open)}
      open={isOpen}
    >
      <summary
        className="relative flex cursor-pointer items-center border-primary-default"
        data-testid="summary"
      >
        <Icon
          className={cn(
            chevron({ state: isOpen ? 'open' : 'default' }),
            '-mr-sm ml-0 !grid size-xl place-items-center rounded-full leading-none transition-all duration-300 ease-out hover:bg-emphasis-strong active:bg-primary-default',
          )}
          size="sm"
          type="chevron-down"
          variant="solid"
        />
        <div className="flex flex-col gap-sm">
          <div className="select-none text-body-md font-bold leading-none">
            <span>{title}</span>
            {isRequired ? <>*</> : null}
          </div>
          {description ? (
            <p className="text-body-sm leading-tight" data-testid="description">
              {description}
            </p>
          ) : null}
        </div>
      </summary>
      <div className="mt-xl">{children}</div>
    </details>
  );
}

PlanSection.displayName = 'PlanSection';
